/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import FieldInput from './FieldInput';
import { ipmApi, errorMessage, useLookup } from '../api';
import { formFields, formatValue, isBlank, nowHHMM, todayISO } from '../fieldTypes';

const defaultsFor = (form) => {
  const values = {};
  for (const f of formFields(form)) {
    if (f.default_today && f.type === 'date') values[f.id] = todayISO();
    if (f.default_today && f.type === 'time') values[f.id] = nowHHMM();
  }
  return values;
};

// Time units convert so "2 days" beats "36 hours"; anything else compares as-is.
const HOURS = { minute: 1 / 60, hour: 1, day: 24, week: 168 };
const hoursPer = (unit) => {
  const u = String(unit || '').toLowerCase().replace(/s$/, '');
  return HOURS[u] ?? null;
};

/**
 * The largest value of `attribute` among the controls picked, in the target
 * field's units — e.g. the longest REI of the products in a treatment.
 */
const autofillValue = (field, picks, controls) => {
  const found = picks
    .map(p => controls.find(c => c._id === p.id)?.data?.[field.autofill.attribute])
    .filter(v => !isBlank(v))
    .map(v => (typeof v === 'object' ? { n: Number(v.value), unit: v.unit } : { n: Number(v), unit: null }))
    .filter(v => Number.isFinite(v.n));
  if (!found.length) return undefined;

  const units = field.units || [];
  const timed = found.every(v => hoursPer(v.unit) !== null);
  const best = found.reduce((a, b) => ((timed ? b.n * hoursPer(b.unit) > a.n * hoursPer(a.unit) : b.n > a.n) ? b : a));
  if (!units.length) return timed ? best.n * hoursPer(best.unit) : best.n;
  const unit = units.includes(best.unit) ? best.unit : units[0];
  const n = timed && hoursPer(unit) ? (best.n * hoursPer(best.unit)) / hoursPer(unit) : best.n;
  return { value: Math.round(n * 100) / 100, unit };
};

/** Controls picked in an autofill source: "fieldId" or "tableFieldId.columnId". */
const picksFor = (source, values) => {
  const [fid, cid] = source.split('.');
  const raw = values[fid];
  const list = cid ? (Array.isArray(raw) ? raw.map(r => r?.[cid]) : []) : (Array.isArray(raw) ? raw : [raw]);
  return list.filter(p => p?.id);
};

/**
 * Renders an admin-defined form. With `entry`, edits that entry; otherwise
 * creates a new one and resets for the next.
 */
const EntryForm = ({ form, entry = null, onSaved, onCancel }) => {
  const [values, setValues] = useState(() => (entry ? { ...entry.data } : defaultsFor(form)));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Values an old entry holds for fields that have since been removed from the form.
  const retired = useMemo(() => {
    if (!entry) return [];
    const current = new Set(formFields(form).map(f => f.id));
    return (entry.fields || []).filter(f => !current.has(f.id) && !isBlank(entry.data?.[f.id]));
  }, [entry, form]);

  // Auto-filled fields recalculate only when the controls picked change, so a
  // value typed over the top stays put — and an old entry isn't rewritten on open.
  const autofilled = useMemo(() => formFields(form).filter(f => f.autofill?.source), [form]);
  const controls = useLookup('controls', autofilled.length > 0);
  const lastPicks = useRef(null);
  useEffect(() => {
    if (!autofilled.length) return;
    const keys = Object.fromEntries(autofilled.map(f => [f.id, picksFor(f.autofill.source, values).map(p => p.id).sort().join()]));
    if (lastPicks.current === null && entry) { lastPicks.current = keys; return; }
    const prev = lastPicks.current || {};
    lastPicks.current = keys;
    if (!controls.length) { lastPicks.current = prev; return; }
    const patch = {};
    for (const f of autofilled) {
      if (keys[f.id] === prev[f.id] || !keys[f.id]) continue;
      const v = autofillValue(f, picksFor(f.autofill.source, values), controls);
      if (v !== undefined) patch[f.id] = v;
    }
    if (Object.keys(patch).length) setValues(cur => ({ ...cur, ...patch }));
  }, [values, controls, autofilled, entry]);

  const set = (id, v) => {
    setValues(prev => ({ ...prev, [id]: v }));
    if (errors[id]) setErrors(prev => ({ ...prev, [id]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const missing = {};
    for (const f of formFields(form)) if (f.required && isBlank(values[f.id])) missing[f.id] = 'Required';
    setErrors(missing);
    if (Object.keys(missing).length) {
      toast.error('Please fill in the required fields.');
      return;
    }
    setSaving(true);
    try {
      const saved = entry
        ? await ipmApi.updateEntry(entry._id, values)
        : await ipmApi.createEntry(form._id, values);
      toast.success(entry ? 'Entry updated' : `${form.name} entry saved`);
      if (!entry) setValues(defaultsFor(form));
      onSaved?.(saved);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save the entry'));
    } finally {
      setSaving(false);
    }
  };

  const sections = form.sections.filter(s => s.fields.length);

  return (
    <Box component="form" onSubmit={submit} noValidate>
      {!sections.length && (
        <Alert severity="info">This form has no fields yet. Use <strong>Manage forms</strong> to add some.</Alert>
      )}
      {sections.map(section => (
        <Paper key={section.id} variant="outlined" className="ipm-section">
          {section.title && <Typography variant="h6" className="ipm-section-title">{section.title}</Typography>}
          {section.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{section.description}</Typography>}
          <Box className="ipm-field-grid">
            {section.fields.map(f => (
              <Box key={f.id} className={f.width === 'full' ? 'ipm-field ipm-field-full' : 'ipm-field'}>
                <FieldInput field={f} value={values[f.id]} onChange={v => set(f.id, v)} error={errors[f.id]} values={values} />
              </Box>
            ))}
          </Box>
        </Paper>
      ))}

      {retired.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This entry also has values for fields that were later removed from the form. They are kept as they were:
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {retired.map(f => <li key={f.id}><strong>{f.label}:</strong> {formatValue(f, entry.data[f.id])}</li>)}
          </ul>
        </Alert>
      )}

      {sections.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {onCancel && <Button onClick={onCancel} disabled={saving}>Cancel</Button>}
          <Button type="submit" variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            {entry ? 'Save changes' : `Save ${form.name} entry`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EntryForm;
